import React from 'react';
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from 'recharts';
import { Painel, Vazio } from './DataCenterKpis';

const TooltipEscuro = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-purple-800/60 bg-[#1a0530] px-3 py-2 text-xs text-white shadow-xl">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p) => <p key={p.dataKey || p.name} style={{ color: p.color }}>{p.name}: <b>{p.value}</b></p>)}
    </div>
  );
};

const fmtDia = (d) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;

export const GraficoEvolucao = ({ dados }) => {
  const temDados = dados?.some((d) => d.criadas || d.instaladas);
  return (
    <Painel titulo="Evolução diária" descricao="Vendas criadas x instaladas x canceladas por dia" className="xl:col-span-2" testid="dc-evolucao">
      {!temDados ? <Vazio /> : (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={dados} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(168,85,247,0.15)" />
              <XAxis dataKey="dia" tickFormatter={fmtDia} tick={{ fontSize: 10, fill: '#9ca3af' }} interval="preserveStartEnd" minTickGap={24} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#9ca3af' }} />
              <Tooltip content={<TooltipEscuro />} labelFormatter={fmtDia} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="criadas" name="Criadas" fill="#7c3aed" radius={[4, 4, 0, 0]} />
              <Bar dataKey="canceladas" name="Canceladas" fill="#ef4444" radius={[4, 4, 0, 0]} />
              <Line type="monotone" dataKey="instaladas" name="Instaladas" stroke="#10b981" strokeWidth={2.5} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </Painel>
  );
};

export const GraficoFunil = ({ funil }) => {
  const max = Math.max(1, ...(funil || []).map((f) => f.quantidade));
  const fmtTempo = (h) => (h == null ? '' : h < 48 ? `${h}h` : `${Math.round(h / 24)}d`);
  return (
    <Painel titulo="Funil por status" descricao="Quantas vendas estão em cada etapa e o tempo médio parado nela" testid="dc-funil">
      {!funil?.length ? <Vazio /> : (
        <ul className="space-y-2">
          {funil.map((f) => (
            <li key={f.status} className="text-xs" data-testid={`dc-funil-${f.status}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="flex items-center gap-2 font-medium text-gray-700 dark:text-gray-200"><span className="h-2.5 w-2.5 rounded-full" style={{ background: f.cor }} />{f.status}</span>
                <span className="flex items-center gap-2 tabular-nums">
                  {f.tempo_medio_horas != null && <span className="rounded bg-gray-100 dark:bg-purple-900/40 px-1.5 py-0.5 text-[10px] text-gray-500 dark:text-purple-200" title="Tempo médio neste status">⌀ {fmtTempo(f.tempo_medio_horas)}</span>}
                  <b>{f.quantidade}</b>
                </span>
              </div>
              <div className="h-2 rounded-full bg-gray-100 dark:bg-purple-950/60 overflow-hidden">
                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${(f.quantidade / max) * 100}%`, background: f.cor }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Painel>
  );
};

export const GraficoResultado = ({ k }) => {
  const dados = k ? [
    { nome: 'Instaladas', valor: k.instaladas, cor: '#10b981' },
    { nome: 'Em andamento', valor: k.em_andamento, cor: '#f59e0b' },
    { nome: 'Devolvidas', valor: k.devolvidas, cor: '#f97316' },
    { nome: 'Canceladas', valor: k.canceladas, cor: '#ef4444' },
  ].filter((d) => d.valor > 0) : [];
  return (
    <Painel titulo="Resultado das vendas" descricao="Distribuição do período" testid="dc-resultado">
      {!dados.length ? <Vazio /> : (
        <div className="h-56 relative">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={dados} dataKey="valor" nameKey="nome" innerRadius={55} outerRadius={85} paddingAngle={3} stroke="none">
                {dados.map((d) => <Cell key={d.nome} fill={d.cor} />)}
              </Pie>
              <Tooltip content={<TooltipEscuro />} />
              <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
            </PieChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center -mt-6">
            <span className="text-2xl font-bold text-gray-900 dark:text-white">{k.conversao}%</span>
            <span className="text-[10px] uppercase tracking-wide text-gray-500">conversão</span>
          </div>
        </div>
      )}
    </Painel>
  );
};

export const ListaRanking = ({ titulo, descricao, itens, chave = 'nome', valor = 'quantidade', cor = '#7c3aed', testid }) => {
  const max = Math.max(1, ...(itens || []).map((i) => i[valor]));
  return (
    <Painel titulo={titulo} descricao={descricao} testid={testid}>
      {!itens?.length ? <Vazio /> : (
        <ul className="space-y-1.5">
          {itens.slice(0, 8).map((i, idx) => (
            <li key={`${i[chave]}-${idx}`} className="text-xs">
              <div className="flex justify-between gap-2"><span className="truncate text-gray-700 dark:text-gray-200">{i[chave]}</span><b className="tabular-nums">{i[valor]}</b></div>
              <div className="h-1.5 rounded-full bg-gray-100 dark:bg-purple-950/60 overflow-hidden mt-0.5"><div className="h-full rounded-full" style={{ width: `${(i[valor] / max) * 100}%`, background: cor }} /></div>
            </li>
          ))}
        </ul>
      )}
    </Painel>
  );
};
